/**
 * SEOToolPage.jsx — SEO独立落地页 for /almanac /dreams /oracle /naming
 * 复用ToolsPage的tab组件, 每个页面带独立meta tags
 */
import React, { useEffect } from "react";
import { API } from "../constants/config";

const GD = "#064E3B";
const GDH = "#D4AF37";
const BG = "#FDFBF7";
const CARD_BG = "#FFFFFF";

// Re-import all the tab components from ToolsPage
// (In a full build these would be extracted to a shared module.
//  For now, inline the lightweight wrappers that fetch data.)

const SEO_META = {
  almanac: {
    title: "老黄历查询 — 每日宜忌·冲煞·28星宿·黄道黑道 | LUMINAARC",
    desc: "免费老黄历万年历查询。每日宜忌、冲煞生肖、二十八星宿、黄道黑道、彭祖百忌、节气。无需登录。",
    h1: "📅 老黄历 · 万年历",
    ogImage: "https://lumarcai.com/og-almanac.png",
    jsonLd: {"@context":"https://schema.org","@type":"WebApplication","name":"老黄历查询","description":"免费老黄历万年历查询。每日宜忌、冲煞生肖、二十八星宿、黄道黑道。","url":"https://lumarcai.com/almanac","applicationCategory":"LifestyleApplication","operatingSystem":"Web","offers":{"@type":"Offer","price":"0","priceCurrency":"USD"}},
  },
  dreams: {
    title: "周公解梦查询 — 梦境解析·原典·现代心理学 | LUMINAARC",
    desc: "免费在线解梦。200+条《周公解梦》原典，结合现代精神分析与场景变调。梦见蛇、水、考试？输入关键词查询。",
    h1: "💭 解梦 · 周公解梦",
    ogImage: "https://lumarcai.com/og-dreams.png",
    jsonLd: {"@context":"https://schema.org","@type":"WebApplication","name":"周公解梦","description":"免费在线解梦。200+条《周公解梦》原典，现代精神分析。","url":"https://lumarcai.com/dreams","applicationCategory":"LifestyleApplication","operatingSystem":"Web","offers":{"@type":"Offer","price":"0","priceCurrency":"USD"}},
  },
  oracle: {
    title: "观音灵签在线抽签 — 100签·诗意解曰仙机 | LUMINAARC",
    desc: "观音灵签100签免费在线抽签。每签含签诗、诗意、解曰、仙机故事、现代白话解读。下下签有心理防护文案。心诚则灵。",
    h1: "🏮 观音灵签 · 在线抽签",
    ogImage: "https://lumarcai.com/og-oracle.png",
    jsonLd: {"@context":"https://schema.org","@type":"WebApplication","name":"观音灵签在线抽签","description":"观音灵签100签免费在线抽签。签诗、诗意、解曰、仙机故事。","url":"https://lumarcai.com/oracle","applicationCategory":"LifestyleApplication","operatingSystem":"Web","offers":{"@type":"Offer","price":"0","priceCurrency":"USD"}},
  },
  naming: {
    title: "AI起名 · 五行八字取名 — 康熙笔画·五格剖象 | LUMINAARC",
    desc: "AI智能起名。520+精选汉字，康熙字典笔画标注，八字五行补缺，五格剖象吉凶。免费在线取名。",
    h1: "✨ AI起名 · 五行八字取名",
    ogImage: "https://lumarcai.com/og-naming.png",
    jsonLd: {"@context":"https://schema.org","@type":"WebApplication","name":"AI起名·五行八字取名","description":"AI智能起名。康熙字典笔画，八字五行补缺，五格剖象吉凶。","url":"https://lumarcai.com/naming","applicationCategory":"LifestyleApplication","operatingSystem":"Web","offers":{"@type":"Offer","price":"0","priceCurrency":"USD"}},
  },
};

function badge(bg, color) {
  return { background: bg, color, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 };
}
const card = { background: CARD_BG, border: `1px solid rgba(212,175,55,0.2)`, borderRadius: 12, padding: 16 };
const bInput = { width: 50, padding: "6px 8px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 13, outline: "none", textAlign: "center" };

// ══════════════════════════════════════════════════════════════════════
// 老黄历 SEO Page
function AlmanacSEO() {
  const [data, setData] = React.useState(null);
  useEffect(() => { fetch(`${API}/tools/almanac`).then(r => r.json()).then(setData); }, []);
  if (!data) return <p style={{ color: "#6B7280" }}>加载中...</p>;
  const rl = data.rigidity_level;
  const rColor = rl === "吉" ? "#059669" : rl === "平" ? "#6B7280" : rl === "慎" ? "#D97706" : rl === "凶" ? "#DC2626" : "#991B1B";
  const rBg = rl === "吉" ? "#ECFDF5" : rl === "平" ? "#F3F4F6" : rl === "慎" ? "#FEF3C7" : rl === "凶" ? "#FEF2F2" : "#7F1D1D";
  return (
    <div>
      <div style={{ ...card, textAlign: "center", marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, color: GD, margin: "0 0 8px" }}>📅 老黄历 · 万年历</h1>
        <div style={{ fontSize: 14, color: "#6B7280" }}>{data.gregorian} 周{data.weekday}</div>
        <div style={{ fontSize: 22, color: GD, fontWeight: 700, margin: "6px 0" }}>{data.lunar} · {data.ganzhi}日</div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 6 }}>
          <span style={{ fontSize: 13, color: "#6B7280" }}>🐲 {data.shengxiao}年</span>
          <span style={badge(data.jianchu === "成" || data.jianchu === "开" ? "#ECFDF5" : data.jianchu === "破" || data.jianchu === "闭" ? "#FEF2F2" : "#F3F4F6", data.jianchu === "成" || data.jianchu === "开" ? "#065F46" : data.jianchu === "破" || data.jianchu === "闭" ? "#991B1B" : "#4B5563")}>{data.jianchu}日</span>
          <span style={{ fontSize: 12, color: "#DC2626", fontWeight: 600 }}>⚡{data.chong_sha}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
          <span style={badge(data.star_28?.fortune === "吉" ? "#ECFDF5" : "#FEF2F2", data.star_28?.fortune === "吉" ? "#065F46" : "#991B1B")}>⭐ {data.star_28?.name}宿({data.star_28?.animal}) · {data.star_28?.group}</span>
          <span style={badge(data.huanghei_dao?.type === "黄道" ? "#ECFDF5" : "#FEF2F2", data.huanghei_dao?.type === "黄道" ? "#065F46" : "#991B1B")}>{data.huanghei_dao?.type === "黄道" ? "🟡" : "⚫"} {data.huanghei_dao?.name}({data.huanghei_dao?.type})</span>
        </div>
        <div style={{ marginTop: 10 }}>
          <span style={badge(rBg, rColor)}>{rl === "吉" ? "✅" : rl === "凶" ? "🚫" : "➖"} 今日{data.rigidity_score}分 · {rl}</span>
          {data.special_bad?.map((sb, i) => <span key={i} style={badge("#FEF2F2", "#DC2626")}>{sb.label}</span>)}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div style={{ ...card, background: "#ECFDF5" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#065F46", marginBottom: 6 }}>✅ 宜</div>
          {(data.yi || []).map((y, i) => <span key={i} style={{ display: "inline-block", background: "#D1FAE5", color: "#065F46", padding: "4px 10px", borderRadius: 12, fontSize: 12, margin: "2px 4px" }}>{y}</span>)}
        </div>
        <div style={{ ...card, background: "#FEF2F2" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#991B1B", marginBottom: 6 }}>⚠️ 忌</div>
          {(data.ji || []).map((j, i) => <span key={i} style={{ display: "inline-block", background: "#FEE2E2", color: "#991B1B", padding: "4px 10px", borderRadius: 12, fontSize: 12, margin: "2px 4px" }}>{j}</span>)}
        </div>
      </div>
      {data.pengzu?.length > 0 && (
        <div style={{ ...card, background: "#FFFDF5" }}>
          <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>📜 彭祖百忌</div>
          {data.pengzu.map((p, i) => <p key={i} style={{ fontSize: 12, color: "#4B5563", margin: "2px 0" }}>{p}</p>)}
        </div>
      )}
      <p style={{ textAlign: "center", fontSize: 12, color: "#9CA3AF", marginTop: 16 }}>
        <a href="/tools" style={{ color: GDH }}>查看更多工具 →</a>
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// 解梦 SEO Page
function DreamsSEO() {
  const [q, setQ] = React.useState("");
  const [r, setR] = React.useState(null);
  const search = async () => {
    if (!q.trim()) return;
    const res = await fetch(`${API}/tools/dreams?keyword=${encodeURIComponent(q)}`);
    setR(await res.json());
  };
  return (
    <div>
      <h1 style={{ fontSize: 24, color: GD, textAlign: "center", marginBottom: 16 }}>💭 解梦 · 周公解梦</h1>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && search()}
          placeholder="输入梦境关键词，如：蛇、水、考试、坠落..."
          style={{ flex: 1, padding: "10px 16px", border: "1px solid #D1D5DB", borderRadius: 10, fontSize: 14, outline: "none" }} />
        <button onClick={search} style={{ padding: "10px 24px", background: GD, color: "#FFF", border: "none", borderRadius: 10, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>🔍 解梦</button>
      </div>
      {r && !r.found && r.keyword ? (
        <div style={card}><p style={{ color: "#6B7280" }}>未找到"{r.keyword}"</p></div>
      ) : r && r.keyword ? (
        <div style={card}>
          <h3 style={{ fontSize: 18, color: GD, margin: "0 0 8px" }}>{r.keyword}</h3>
          <p style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic", marginBottom: 8 }}>📖 {r.classical}</p>
          <p style={{ fontSize: 14, color: "#4B5563", lineHeight: 1.8 }}>{r.modern}</p>
        </div>
      ) : (
        <p style={{ textAlign: "center", color: "#6B7280", fontSize: 13 }}>输入关键词开始查询 · 200+条《周公解梦》原典</p>
      )}
      <p style={{ textAlign: "center", fontSize: 12, color: "#9CA3AF", marginTop: 16 }}>
        <a href="/tools" style={{ color: GDH }}>查看更多工具 →</a>
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// 观音灵签 SEO Page
function OracleSEO() {
  const [r, setR] = React.useState(null);
  const draw = async () => {
    const res = await fetch(`${API}/tools/divination`);
    setR(await res.json());
  };
  const fc = (f) => { if (f === "大吉") return "#059669"; if (f.includes("吉")) return GDH; if (f === "下下") return "#DC2626"; return "#6B7280"; };
  return (
    <div>
      <h1 style={{ fontSize: 24, color: GD, textAlign: "center", marginBottom: 16 }}>🏮 观音灵签 · 在线抽签</h1>
      <div style={{ ...card, textAlign: "center", marginBottom: 16 }}>
        <p style={{ fontSize: 14, color: "#6B7280", marginBottom: 12 }}>心中默念问题，然后抽签。100签全量·心诚则灵。</p>
        <button onClick={draw} style={{ padding: "16px 48px", background: "#DC2626", color: "#FFF", border: "none", borderRadius: 16, fontSize: 18, cursor: "pointer", fontWeight: 700 }}>🏮 抽 签</button>
      </div>
      {r && (
        <div style={{ ...card, textAlign: "center" }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: r.fortune === "大吉" ? "#ECFDF5" : r.fortune === "下下" ? "#FEF2F2" : "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", border: `3px solid ${fc(r.fortune)}` }}>
            <span style={{ fontSize: 32, fontWeight: 700, color: fc(r.fortune) }}>{r.lot_num}</span>
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: fc(r.fortune), marginBottom: 8 }}>第{r.lot_num}签 · {r.fortune}</div>
          <div style={{ background: "#F9FAFB", padding: "14px", borderRadius: 8, fontSize: 14, color: "#4B5563", lineHeight: 2, fontStyle: "italic", marginBottom: 10 }}>{r.poem}</div>
          {r.story && <p style={{ fontSize: 12, color: "#6B7280", marginBottom: 8 }}>🏯 {r.story}</p>}
          {r.modern_translation && <p style={{ fontSize: 13, color: "#4B5563", lineHeight: 1.8, background: "#FFFDF5", padding: "12px", borderRadius: 8 }}>💬 {r.modern_translation}</p>}
          {r.is_ominous && r.protection && (
            <div style={{ background: "#FFF5F5", border: "1px solid #FECACA", borderRadius: 12, padding: "16px", marginTop: 12, textAlign: "left" }}>
              <div style={{ fontSize: 13, color: "#991B1B", fontWeight: 700, marginBottom: 8 }}>🛡️ 心灵护盾</div>
              <p style={{ fontSize: 13, color: "#7F1D1D", lineHeight: 1.8, margin: 0 }}>{r.protection}</p>
            </div>
          )}
        </div>
      )}
      <p style={{ textAlign: "center", fontSize: 12, color: "#9CA3AF", marginTop: 16 }}>
        <a href="/tools" style={{ color: GDH }}>查看更多工具 →</a>
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// AI起名 SEO Page
function NamingSEO() {
  const [surname, setSurname] = React.useState("");
  const [names, setNames] = React.useState([]);
  const generate = async () => {
    if (!surname.trim()) return;
    const fd = new URLSearchParams();
    fd.append("surname", surname);
    fd.append("count", "6");
    const r = await fetch(`${API}/tools/naming`, { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: fd.toString() });
    setNames((await r.json()).names || []);
  };
  const ec = (e) => ({ "木": "#059669", "火": "#DC2626", "土": "#D97706", "金": GDH, "水": "#2563EB" })[e] || "#6B7280";
  return (
    <div>
      <h1 style={{ fontSize: 24, color: GD, textAlign: "center", marginBottom: 16 }}>✨ AI起名 · 五行八字取名</h1>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input value={surname} onChange={e => setSurname(e.target.value)} onKeyDown={e => e.key === "Enter" && generate()}
          placeholder="输入姓氏..." style={{ flex: 1, padding: "10px 16px", border: "1px solid #D1D5DB", borderRadius: 10, fontSize: 14, outline: "none" }} />
        <button onClick={generate} style={{ padding: "10px 24px", background: GD, color: "#FFF", border: "none", borderRadius: 10, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>✨ 起名</button>
      </div>
      {names.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {names.map((n, i) => (
            <div key={i} style={{ ...card, textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: GD, fontFamily: "'Noto Serif SC',serif" }}>{n.name}</div>
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                <span style={badge("#F3F4F6", ec(n.mid_element))}>{n.mid_char} {n.mid_element} {n.mid_stroke}画</span>
                <span style={badge("#F3F4F6", ec(n.tail_element))}>{n.tail_char} {n.tail_element} {n.tail_stroke}画</span>
              </div>
              <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>总{n.total_strokes}画</div>
              {n.wuge && (
                <div style={{ display: "flex", justifyContent: "center", gap: 3, marginTop: 4 }}>
                  {["tian", "ren", "di", "zong", "wai"].map(k => {
                    const w = n.wuge[k];
                    const labels = { tian: "天", ren: "人", di: "地", zong: "总", wai: "外" };
                    return <span key={k} style={{ fontSize: 9, padding: "1px 5px", borderRadius: 6, background: w.is_ji ? "#ECFDF5" : "#FEF2F2", color: w.is_ji ? "#065F46" : "#991B1B" }} title={`${labels[k]}格${w.num}画: ${w.ji}`}>{labels[k]}{w.num}{w.is_ji ? "✓" : "✗"}</span>;
                  })}
                </div>
              )}
              <p style={{ fontSize: 11, color: "#6B7280", margin: "4px 0 0", lineHeight: 1.5 }}>{n.meaning}</p>
            </div>
          ))}
        </div>
      )}
      <p style={{ textAlign: "center", fontSize: 12, color: "#9CA3AF", marginTop: 16 }}>
        <a href="/tools" style={{ color: GDH }}>查看更多工具 →</a>
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
export default function SEOToolPage({ tool }) {
  const meta = SEO_META[tool] || {};
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = meta.title || "LUMINAARC";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", meta.desc || "");
    // og:image
    if (meta.ogImage) {
      let og = document.querySelector('meta[property="og:image"]');
      if (!og) { og = document.createElement("meta"); og.setAttribute("property","og:image"); document.head.appendChild(og); }
      og.setAttribute("content", meta.ogImage);
    }
    // JSON-LD
    if (meta.jsonLd) {
      let ld = document.getElementById("ld-json-seo");
      if (!ld) { ld = document.createElement("script"); ld.id = "ld-json-seo"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
      ld.textContent = JSON.stringify(meta.jsonLd);
    }
  }, [tool]);

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "40px 20px 80px", background: BG, minHeight: "100vh" }}>
      {tool === "almanac" && <AlmanacSEO />}
      {tool === "dreams" && <DreamsSEO />}
      {tool === "oracle" && <OracleSEO />}
      {tool === "naming" && <NamingSEO />}

      {/* 命盘引流条 — 统一跨站引流 */}
      <div style={{
        marginTop: 32, padding: "20px 24px",
        background: "linear-gradient(135deg,rgba(212,175,55,.06),rgba(6,78,59,.04))",
        borderRadius: 14,
        border: "1px solid rgba(212,175,55,.22)",
        textAlign: "center",
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#8B6914", marginBottom: 6 }}>
          🔮 想了解你自己的命盘吗？
        </div>
        <p style={{ fontSize: 13, color: "#64748B", lineHeight: 1.7, maxWidth: 480, margin: "0 auto 14px" }}>
          八字+紫微双引擎深度解读——日主强弱、十神性格、大运流年、十二宫全分析。引擎真算，不是AI凭空生成。
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
          <a href="/audit" style={{
            display: "inline-block", padding: "10px 24px",
            background: "linear-gradient(135deg,#D4AF37,#064E3B)",
            color: "#fff", borderRadius: 10, fontSize: 14, fontWeight: 600,
            textDecoration: "none", letterSpacing: ".03em",
          }}>
            免费试排 →
          </a>
          <span style={{ fontSize: 12, color: "#94A3B8" }}>无需信用卡 · 2分钟出结果</span>
        </div>
      </div>
    </div>
  );
}
